import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ICustomer } from '../../core/interfaces/ICustomer';
import { BaseFormComponent } from '../../../../core/layout/components/abstracts/base-form.component';
import { FormFields } from '../../../../core/types';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { FormControlComponent } from '../../../../shared/components/form-control/form-control.component';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { SelectComponent } from '../../../../shared/components/select/select.component';
import { DictionariesStore } from '../../../../core/stores/DictionariesStore';
import { Dictionaries } from '../../../../core/enums/Dictionaries';
import { DatepickerComponent } from '../../../../shared/components/datepicker/datepicker.component';
import { MaxWidthDirective } from '../../../../shared/directives/max-width.directive';
import { IIdAndName } from '../../../../core/interfaces/IIdAndName';
import { IdAndNamePipe } from '../../../../shared/pipes/id-and-name.pipe';
import { MinHeightDirective } from '../../../../shared/directives/min-height.directive';
import { MaxHeightDirective } from '../../../../shared/directives/max-height.directive';
import { ContentBoxComponent } from '../../../../shared/components/content-box/content-box.component';
import { AddNoteModalComponent } from '../../../../shared/components/add-note-modal/add-note-modal.component';
import { CustomersService } from '../../core/services/CustomersService';
import { finalize } from 'rxjs';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { LowerCasePipe } from '@angular/common';

@Component({
  selector: 'app-edit-customer-details-form',
  standalone: true,
  imports: [
    TranslatePipe,
    FormControlComponent,
    ReactiveFormsModule,
    SelectComponent,
    DatepickerComponent,
    MaxWidthDirective,
    IdAndNamePipe,
    MinHeightDirective,
    MaxHeightDirective,
    ContentBoxComponent,
    AddNoteModalComponent,
    LowerCasePipe,
  ],
  templateUrl: './edit-customer-details-form.component.html',
  styleUrl: './edit-customer-details-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditCustomerDetailsFormComponent extends BaseFormComponent<ICustomer> {

  public readonly titleOptions: IIdAndName[] = [
    {
      id: 'Frau'.trans('customers/forms/add-edit-customer-form/title-options.Frau'),
      name: 'Frau'.trans('customers/forms/add-edit-customer-form/title-options.Frau')
    },
    {
      id: 'Herr'.trans('customers/forms/add-edit-customer-form/title-options.Herr'),
      name: 'Herr'.trans('customers/forms/add-edit-customer-form/title-options.Herr')
    },
    {
      id: 'Firma'.trans('customers/forms/add-edit-customer-form/title-options.Firma'),
      name: 'Firma'.trans('customers/forms/add-edit-customer-form/title-options.Firma')
    }
  ];

  protected override readonly formFields: FormFields = {
    email: new FormControl(null, Validators.compose([Validators.email, Validators.required])),
    firstName: new FormControl(null, Validators.required),
    lastName: new FormControl(null, Validators.required),
    street: new FormControl(null),
    city: new FormControl(null),
    countryId: new FormControl(null, Validators.required),
    postalCode: new FormControl(null),
    nickname: new FormControl(null),
    birthDate: new FormControl(null),
    title: new FormControl(null, Validators.required),
    companyName: new FormControl(null),
    areaCode: new FormControl(null),
    phoneNumber: new FormControl(null),
    prefLanguageId: new FormControl(null),
    prefCurrencyId: new FormControl(null),
    remark: new FormControl(null),
  };

  private readonly _dictionariesStore = inject(DictionariesStore);
  private readonly _customersService = inject(CustomersService);

  public readonly currencies = this._dictionariesStore[Dictionaries.CURRENCIES];
  public readonly languages = this._dictionariesStore[Dictionaries.LANGUAGE];
  public readonly countries = this._dictionariesStore[Dictionaries.COUNTRIES];

  public resetPassword(): void {
    this._customersService.resetPassword(this.data.email)
      .subscribe({
        next: () => {
          this.notificationService.pushSuccess(
            'Erfolg'.trans('notifications/success.Title'),
            'Password reset correctly.'.trans(`reset-customer-password/notifications/success.Message`)
          );
        },
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`reset-customer-password/notifications/${error.message}.Message`)
          );
        }
      });
  }

  protected override formValidActions(): void {
    const customer: ICustomer = this.form.value;
    customer.id = this.data.id;

    this._customersService.updateCustomer(customer)
      .pipe(
        finalize(() => this.unlockForm())
      )
      .subscribe({
        next: () => {
          this.notificationService.pushSuccess(
            'Erfolg'.trans('notifications/success.Title'),
            'Customer details updated correctly.'.trans(`update-customer/notifications/success.Message`)
          );

          this.formFinished.emit(
            {
              customer: `${customer.lastName}, ${customer.firstName}`,
              companyName: customer.companyName
            }
          );
        },
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`update-customer/notifications/${error.message}.Message`)
          );
        }
      });
  }
}
